import {
  getListings,
  getListingsById,
  getMemberships,
  getMembershipsByMembershipId,
} from "api/methods";
import { Models } from "api/types";
import { STALE_TIME } from "api/staleTimes";
import { queryClient } from "pages/Root";

import { getUnlockedShopItems } from "./unlockedShopItems";

type Listing = Models<"ListingModel">;
type Membership = Awaited<ReturnType<typeof getMemberships>>[number];

/**
 * Re-fetches the unlisted listings the patient has unlocked. They are absent from the catalog
 * response by design, so the shop asks for them by id and merges them in.
 *
 * A failed fetch is skipped for this render but deliberately *not* forgotten. No status here is
 * authoritative about the item being gone: a 404 is equally what a patient sees after switching to
 * a location that does not sell the item, and a transient network error would otherwise cost them
 * an unlock they cannot recover without the original link. The TTL and the entry cap do the
 * cleanup instead.
 */
/**
 * Ids of unlocked MyProducts listings. The products shop reads its own endpoint with its own item
 * shape, so these are re-admitted server-side by that read rather than merged client-side — a
 * listing fetched by id would be the wrong shape for the products surface.
 */
export const getUnlockedProductListingIds = (): string[] =>
  getUnlockedShopItems()
    .filter((entry) => entry.kind === "product")
    .map((entry) => entry.id);

export const fetchUnlockedListings = async (): Promise<Listing[]> => {
  const entries = getUnlockedShopItems().filter((entry) => entry.kind === "listing");

  const results = await Promise.all(
    entries.map((entry) =>
      queryClient
        .fetchQuery({ ...getListingsById.keyed({ id: entry.id }), staleTime: 1000 * 60 })
        .catch(() => null),
    ),
  );

  return results.filter((listing): listing is Listing => listing !== null);
};

export const fetchUnlockedMemberships = async (): Promise<Membership[]> => {
  const entries = getUnlockedShopItems().filter((entry) => entry.kind === "membership");

  const results = await Promise.all(
    entries.map((entry) =>
      queryClient
        .fetchQuery({
          ...getMembershipsByMembershipId.keyed({ membershipId: entry.id }),
          staleTime: 1000 * 60,
        })
        .catch(() => null),
    ),
  );

  return results.filter((membership): membership is Membership => membership !== null);
};

/**
 * Appends unlocked items to a catalog response, skipping any the catalog already carries (an
 * item that was un-unlisted since the link was opened).
 */
export const mergeUnlockedItems = <T extends { id: string }>(
  catalog: readonly T[] | undefined,
  unlocked: readonly T[],
): T[] => {
  const catalogItems = catalog ?? [];
  if (unlocked.length === 0) return [...catalogItems];

  const known = new Set(catalogItems.map((item) => item.id));

  return [...catalogItems, ...unlocked.filter((item) => !known.has(item.id))];
};

/**
 * Query definitions for "catalog plus whatever this patient has unlocked".
 *
 * These deliberately carry their own keys. Overriding `queryFn` on `getListings.keyed()` stored
 * merged data under the key every other caller uses for the raw server catalog, so whichever query
 * ran first decided what the others saw. The key also includes the unlocked ids, so opening a new
 * link produces a different key rather than silently reusing a cached list without that item.
 */
const unlockedIdsFor = (kind: "listing" | "membership") =>
  getUnlockedShopItems()
    .filter((entry) => entry.kind === kind)
    .map((entry) => entry.id)
    .sort();

/**
 * Only the unlisted listings this patient unlocked by link — what the Algolia-backed shop surfaces
 * merge in, since an unlisted record is indexed as `isVisibleInShop:false` and never comes back
 * from the catalog queries.
 */
export const unlockedListingsQuery = () => ({
  queryKey: ["shop/unlocked-listings", unlockedIdsFor("listing")] as const,
  queryFn: fetchUnlockedListings,
  staleTime: 1000 * 60,
});

export const listingsWithUnlockedQuery = () => ({
  queryKey: ["shop/listings-with-unlocked", unlockedIdsFor("listing")] as const,
  queryFn: () =>
    Promise.all([
      queryClient.fetchQuery({ ...getListings.keyed(), staleTime: STALE_TIME.catalog }),
      fetchUnlockedListings(),
    ]).then(([catalog, unlocked]) => mergeUnlockedItems(catalog, unlocked)),
  staleTime: STALE_TIME.catalog,
});

export const membershipsWithUnlockedQuery = () => ({
  queryKey: ["shop/memberships-with-unlocked", unlockedIdsFor("membership")] as const,
  queryFn: () =>
    Promise.all([getMemberships(), fetchUnlockedMemberships()]).then(([catalog, unlocked]) =>
      mergeUnlockedItems(catalog, unlocked),
    ),
});
